import { type MutableRefObject, useRef, useState, useCallback } from 'react';
import { type CarouselControls } from './Carousel';

export interface UseCarouselControlsOptions {
  autoPlay?: boolean;
}

export type UseCarouselControlsReturn = {
  /** Ref to pass to Carousel's carouselRef prop */
  ref: MutableRefObject<CarouselControls | null>;
  onSlideChange: () => void;
  next: () => void;
  back: () => void;
  goToSlide: (index: number) => void;
  startAutoPlay: () => void;
  stopAutoPlay: () => void;
  getIsNextDisabled: () => boolean;
  getIsBackDisabled: () => boolean;
  getIsDragging: () => boolean;
  getIsAutoPlayEnabled: () => boolean;
  getIsInfinite: () => boolean;
  getActiveIndexes: () => number[];
  getFirstVisibleIndex: () => number;
};

/**
 * Hook for controlling a Carousel externally with reactive state.
 *
 * Wraps the carouselRef pattern and handles state synchronization automatically.
 * Use this when you need navigation controls outside the Carousel component.
 *
 * @param options.autoPlay - Set to true if autoplay is enabled on the Carousel.
 *   This ensures getIsAutoPlayEnabled() returns the correct value on first
 *   render before the carousel mounts.
 *
 * @example
 * ```tsx
 * // For autoplay carousels, pass autoPlay to show correct icon on first render
 * const carousel = useCarouselControls({ autoPlay: true });
 *
 * <IconButton
 *   icon={carousel.getIsAutoPlayEnabled() ? <Pause /> : <Play />}
 *   onClick={carousel.getIsAutoPlayEnabled() ? carousel.stopAutoPlay : carousel.startAutoPlay}
 * />
 *
 * <Carousel
 *   autoPlay
 *   carouselRef={carousel.ref}
 *   onSlideChange={carousel.onSlideChange}
 * >
 *   {(state) => <CarouselTrack>...</CarouselTrack>}
 * </Carousel>
 * ```
 */
export function useCarouselControls(
  options: UseCarouselControlsOptions = {}
): UseCarouselControlsReturn {
  const { autoPlay = false } = options;

  const ref = useRef<CarouselControls | null>(null);
  const [, forceRefUpdate] = useState(0);

  const onSlideChange = useCallback(() => {
    forceRefUpdate((n) => n + 1);
  }, []);

  return {
    ref,
    onSlideChange,
    next: () => {
      ref.current?.next();
      onSlideChange();
    },
    back: () => {
      ref.current?.back();
      onSlideChange();
    },
    goToSlide: (index: number) => {
      ref.current?.goToSlide(index);
      onSlideChange();
    },
    startAutoPlay: () => {
      ref.current?.startAutoPlay();
      onSlideChange();
    },
    stopAutoPlay: () => {
      ref.current?.stopAutoPlay();
      onSlideChange();
    },
    getIsNextDisabled: () => ref.current?.isNextDisabled ?? false,
    getIsBackDisabled: () => ref.current?.isBackDisabled ?? true,
    getIsDragging: () => ref.current?.isDragging ?? false,
    getIsAutoPlayEnabled: () => ref.current?.isAutoPlayEnabled ?? autoPlay,
    getIsInfinite: () => ref.current?.isInfinite ?? false,
    getActiveIndexes: () => ref.current?.activeIndexes ?? [],
    getFirstVisibleIndex: () => ref.current?.firstVisibleIndex ?? 0,
  };
}
